import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

import type { RewriteChoice } from './regeneration';

/**
 * Write something again, with an instruction — spec 013 §12.1, widened by GEN-47.
 *
 * **The warning comes before the click.** A scene placed in four lessons is forked rather
 * than rewritten, and a reviewer who does not know that looks for their change in the
 * wrong course.
 *
 * The instruction is required: a regenerate button with nothing to say is a re-roll, and
 * a re-roll of prose produces four drafts and no decision. One picker and one button
 * rather than a button per grain — what is chosen is a value, not five actions.
 */
export function Regenerate({
  choices,
  warning,
  disabled,
  onRegenerate,
}: {
  choices: RewriteChoice[];
  warning?: string | undefined;
  disabled: boolean;
  onRegenerate: (choice: RewriteChoice, instruction: string) => void;
}) {
  const [instruction, setInstruction] = useState('');
  const [key, setKey] = useState(choices[0]?.key ?? '');

  const chosen = choices.find((choice) => choice.key === key) ?? choices[0];

  if (chosen === undefined) return null;

  const ready = instruction.trim() !== '' && !disabled;

  return (
    <Stack spacing={1}>
      {warning !== undefined && <Alert severity="warning">{warning}</Alert>}

      {choices.length > 1 && (
        <TextField
          select
          label="What should be written again?"
          value={chosen.key}
          size="small"
          fullWidth
          onChange={(event) => {
            setKey(event.target.value);
          }}
        >
          {choices.map((choice) => (
            <MenuItem key={choice.key} value={choice.key}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        label="What should be different?"
        value={instruction}
        size="small"
        fullWidth
        multiline
        onChange={(event) => {
          setInstruction(event.target.value);
        }}
      />

      <Button
        variant="outlined"
        disabled={!ready}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          onRegenerate(chosen, instruction.trim());
          setInstruction('');
        }}
      >
        {choices.length > 1 ? 'Regenerate' : chosen.label}
      </Button>
    </Stack>
  );
}
